import {useAuth} from '@common/auth/use-auth';
import {useChangeLocale} from '@common/locale-switcher/change-locale';
import {CustomMenu, CustomMenuItem} from '@common/menus/custom-menu';
import {MenuItemConfig} from '@common/menus/menu-config';
import {useCustomMenu} from '@common/menus/use-custom-menu';
import {NotificationDialogTrigger} from '@common/notifications/dialog/notification-dialog-trigger';
import {Logo} from '@common/ui/navigation/navbar/logo';
import {NavbarAuthButtons} from '@common/ui/navigation/navbar/navbar-auth-buttons';
import {
  NavbarAuthUser,
  NavbarAuthUserProps,
} from '@common/ui/navigation/navbar/navbar-auth-user';
import {useNavigate} from '@common/ui/navigation/use-navigate';
import {getBootstrapData, useBootstrapDataStore} from '@ui/bootstrap-data/bootstrap-data-store';
import {ButtonColor} from '@ui/buttons/get-shared-button-style';
import {IconButton} from '@ui/buttons/icon-button';
import {Item} from '@ui/forms/listbox/item';
import {useSelectedLocale} from '@ui/i18n/selected-locale';
import {Trans} from '@ui/i18n/trans';
import {createSvgIconFromTree} from '@ui/icons/create-svg-icon';
import {LanguageIcon} from '@ui/icons/material/Language';
import {ExpandMoreIcon} from '@ui/icons/material/ExpandMore';
import {MenuIcon} from '@ui/icons/material/Menu';
import {ShoppingCartIcon} from '@ui/icons/material/ShoppingCart';
import {Menu, MenuItem, MenuTrigger} from '@ui/menu/menu-trigger';
import {useSettings} from '@ui/settings/use-settings';
import {useDarkThemeVariables} from '@ui/themes/use-dark-theme-variables';
import {useIsDarkMode} from '@ui/themes/use-is-dark-mode';
import {useMediaQuery} from '@ui/utils/hooks/use-media-query';
import {isAbsoluteUrl} from '@ui/utils/urls/is-absolute-url';
import clsx from 'clsx';
import {ReactElement, ReactNode} from 'react';
import {useCart, useCartUi} from '@app/checkout/checkout-queries';
import {getAppearance} from '@app/appearance/appearance-data';

/** Active theme's header style (CSS hook for the navbar wrapper). */
function getNavbarHeaderStyle(): string {
  return (getAppearance().theme?.options as any)?.header_style || 'classic';
}

type NavbarColor =
  | 'primary'
  | 'bg'
  | 'bg-alt'
  | 'transparent'
  | 'bg-elevated'
  | string;

export interface NavbarProps {
  hideLogo?: boolean | null;
  toggleButton?: ReactElement;
  children?: ReactNode;
  className?: string;
  color?: NavbarColor;
  bgOpacity?: number | string;
  darkModeColor?: NavbarColor;
  logoColor?: 'dark' | 'light' | 'matchMode';
  textColor?: string;
  primaryButtonColor?: ButtonColor;
  border?: string;
  size?: 'xs' | 'sm' | 'md' | null;
  rightChildren?: ReactNode;
  menuPosition?: string;
  authMenuItems?: NavbarAuthUserProps['items'];
  alwaysDarkMode?: boolean;
  wrapInContainer?: boolean;
  // Suppress the mobile hamburger for the nav menu. Used by sidebar/dashboard
  // layouts where the sidebar (and its own toggle) already provides mobile
  // navigation, so a second hamburger would just crowd the bar.
  hideMobileMenu?: boolean;
}
export function Navbar(props: NavbarProps) {
  let {
    hideLogo,
    toggleButton,
    children,
    className,
    border,
    size = 'md',
    color = 'bg',
    textColor,
    darkModeColor,
    rightChildren,
    // Default to the main "header" menu so every page shows the same nav links
    // and mobile hamburger. Pages that want a different menu pass it explicitly
    // (agent-mailbox, billing-page, …); the menu renders nothing if empty.
    menuPosition = 'header',
    primaryButtonColor,
    authMenuItems,
    logoColor,
    alwaysDarkMode = false,
    wrapInContainer = false,
    hideMobileMenu = false,
  } = props;
  const isDarkMode = useIsDarkMode() || alwaysDarkMode;
  // Below `lg` the nav links collapse into the hamburger and the avatar drops
  // its name (compact), so the right-edge icons never overflow / clip the
  // avatar on tablet-width screens.
  const isCompactViewport = useMediaQuery('(max-width: 1023px)');
  const {notifications} = useSettings();
  const {isLoggedIn} = useAuth();
  const darkThemeVars = useDarkThemeVariables();
  const showNotifButton = isLoggedIn && notifications?.integrated;
  color = color ?? 'primary';
  darkModeColor = darkModeColor ?? 'bg-elevated';

  if (logoColor === 'matchMode') {
    logoColor = isDarkMode ? 'light' : 'dark';
  }

  if (isDarkMode) {
    color = darkModeColor;
  }

  return (
    <div
      // Active theme header style — used as a CSS hook (data-header-style) for
      // style variants (centered logo, transparent, minimal…) without changing
      // the navbar's structure, so it stays non-breaking.
      data-header-style={getNavbarHeaderStyle()}
      style={alwaysDarkMode ? darkThemeVars : undefined}
      className={clsx(
        getColorStyle(color, textColor),
        size === 'md' && 'h-64 py-8',
        size === 'sm' && 'h-54 py-4',
        size === 'xs' && 'h-48 py-4',
        border,
        className,
      )}
    >
      <div
        className={clsx(
          // No justify-end here — the ms-auto on the actions group is
          // what positions the right-edge cluster. justify-end was
          // packing everything together and eating the gap between menu
          // and actions.
          'flex h-full items-center gap-10 px-14 md:px-20',
          wrapInContainer && 'container mx-auto',
        )}
      >
        {/* The sidebar toggle (dashboard layouts) and the nav hamburger both
            sit at the very start, before the logo. On desktop the hamburger is
            lg:hidden so the logo leads; sidebar layouts pass hideMobileMenu to
            drop the hamburger entirely and keep only the toggle. */}
        {toggleButton}
        {!hideMobileMenu && <MobileMenu position={menuPosition} />}
        {!hideLogo && (
          <Logo
            size="h-full max-h-26 md:max-h-36"
            // Logical margin so the gap sits after the logo in both
            // LTR (right of logo) and RTL (left of logo).
            className="me-4 shrink-0 md:me-24"
            color={resolveLogoColor({
              navbarColor: color,
              logoColor,
              isDarkMode,
            })}
          />
        )}
        {children}
        <DesktopMenu position={menuPosition} />
        {/* Actions cluster:
            - OUTER wrapper inherits the parent dir (LTR or RTL) and its
              `ms-auto` pushes the whole group to the trailing edge of the bar
              in BOTH directions.
            - The secondary icons (bell → language → cart) live in a dir="ltr"
              island so their visual order stays fixed regardless of direction.
            - The avatar/auth is the LAST child of the outer (direction-aware)
              wrapper, so it lands at the very end edge of the bar at every
              breakpoint — right in LTR, left in RTL. */}
        <div className="ms-auto flex shrink-0 items-center gap-4 md:gap-14">
          <div dir="ltr" className="flex items-center gap-4 md:gap-14">
            {rightChildren}
            {showNotifButton && <NotificationDialogTrigger />}
            <NavbarLocaleSwitcher />
            <NavbarCartButton />
          </div>
          {isLoggedIn ? (
            <NavbarAuthUser
              variant={isCompactViewport ? 'compact' : 'wide'}
              items={authMenuItems}
            />
          ) : (
            <NavbarAuthButtons
              navbarColor={color}
              primaryButtonColor={primaryButtonColor}
            />
          )}
        </div>
      </div>
    </div>
  );
}

interface DesktopMenuProps {
  position: NavbarProps['menuPosition'];
}
function DesktopMenu({position}: DesktopMenuProps) {
  return (
    <CustomMenu
      className="mx-14 text-sm max-lg:hidden"
      itemClassName={isActive =>
        clsx(
          'opacity-90 hover:underline hover:opacity-100',
          isActive && 'opacity-100',
        )
      }
      menu={position}
    >
      {/* A configured group becomes a dropdown instead of a link to nowhere:
          a group carries no action, so rendering it as an item gave a header
          entry that navigated to "". Grouping is how a header with a dozen
          destinations stays readable. */}
      {(item, itemProps) =>
        item.type === 'group' && item.children?.length ? (
          <NavbarMenuGroup key={item.id} item={item} />
        ) : (
          <CustomMenuItem key={item.id} {...itemProps} />
        )
      }
    </CustomMenu>
  );
}

/**
 * The header menu, rendered the way the public pages render it: a group becomes
 * a dropdown instead of a dead link. The dashboard and admin top bars use this
 * too, so the back-office shows the SAME header navigation as the front pages
 * (they used to render every item flat, hiding each group's children).
 */
export function NavbarCustomMenu({
  menu = 'header',
  className,
  itemClassName,
  gap,
  buttonClassName,
}: {
  menu?: NavbarProps['menuPosition'];
  className?: string;
  itemClassName?: string | ((props: {isActive: boolean; item: MenuItemConfig}) => string | undefined);
  gap?: string;
  buttonClassName?: string;
}) {
  return (
    <CustomMenu className={className} itemClassName={itemClassName} gap={gap} menu={menu}>
      {(item, itemProps) =>
        item.type === 'group' && item.children?.length ? (
          <NavbarMenuGroup key={item.id} item={item} className={buttonClassName} />
        ) : (
          <CustomMenuItem key={item.id} {...itemProps} />
        )
      }
    </CustomMenu>
  );
}

function NavbarMenuGroup({item, className}: {item: MenuItemConfig; className?: string}) {
  const navigate = useNavigate();
  const Icon = item.icon && createSvgIconFromTree(item.icon);

  const go = (child: MenuItemConfig) => {
    if (isAbsoluteUrl(child.action)) {
      window.open(child.action, child.target)?.focus();
    } else {
      navigate(child.action);
    }
  };

  return (
    <MenuTrigger>
      <button
        type="button"
        className={
          className ??
          'flex items-center gap-4 text-sm opacity-90 hover:underline hover:opacity-100'
        }
      >
        {Icon && <Icon size="sm" />}
        <Trans message={item.label} />
        <ExpandMoreIcon size="sm" />
      </button>
      <Menu>
        {(item.children ?? []).map(child => {
          const ChildIcon = child.icon && createSvgIconFromTree(child.icon);
          return (
            <Item
              key={child.id}
              value={child.action}
              onSelected={() => go(child)}
              startIcon={ChildIcon && <ChildIcon />}
            >
              <Trans message={child.label} />
            </Item>
          );
        })}
      </Menu>
    </MenuTrigger>
  );
}

interface MobileMenuProps {
  position: NavbarProps['menuPosition'];
}
function MobileMenu({position}: MobileMenuProps) {
  const navigate = useNavigate();
  const menu = useCustomMenu(position);

  if (!menu?.items.length) {
    return null;
  }

  const handleItemClick = (item: MenuItemConfig) => {
    if (isAbsoluteUrl(item.action)) {
      window.open(item.action, item.target)?.focus();
    } else {
      navigate(item.action);
    }
  };

  return (
    <MenuTrigger>
      <IconButton className="lg:hidden" aria-label="Toggle menu">
        <MenuIcon />
      </IconButton>
      <Menu>
        {/* Flattened: a phone menu is already a vertical list, so a group adds
            a tap without adding information. The group's own row is dropped —
            it has no action to take. */}
        {menu.items
          .flatMap(item =>
            item.type === 'group' && item.children?.length
              ? item.children
              : [item],
          )
          .map(item => {
            const Icon = item.icon && createSvgIconFromTree(item.icon);
            return (
              <Item
                value={item.action}
                onSelected={() => handleItemClick(item)}
                key={item.id}
                startIcon={Icon && <Icon />}
              >
                <Trans message={item.label} />
              </Item>
            );
          })}
      </Menu>
    </MenuTrigger>
  );
}

function NavbarCartButton() {
  // The cart store lives in the project app; using @app/* alias so this
  // navbar (which is common/foundation) can open the same drawer used by
  // the dashboard. Falls back to /services navigation if the store ever
  // fails to load (defensive only — the global CartDrawer should always
  // be mounted via app-router).
  const toggleCart = useCartUi(s => s.toggle);
  const {data: cart} = useCart();
  const count = cart?.items?.length ?? 0;
  return (
    <span className="relative inline-block">
      <IconButton aria-label="Cart" onClick={toggleCart}>
        <ShoppingCartIcon />
      </IconButton>
      {count > 0 && (
        <span className="pointer-events-none absolute -end-2 -top-2 flex h-16 min-w-16 items-center justify-center rounded-full bg-danger px-4 text-2xs font-medium text-white">
          {count}
        </span>
      )}
    </span>
  );
}

// Language → country flag code mapping (en → us, ar → eg, …).
const NAV_LANG_FLAG_MAP: Record<string, string> = {
  en: 'us', ar: 'eg', zh: 'cn', ja: 'jp', ko: 'kr', he: 'il',
  cs: 'cz', el: 'gr', fa: 'ir', uk: 'ua', vi: 'vn', ms: 'my',
};
function navFlagFor(lang: string): string {
  const lc = lang.toLowerCase();
  return NAV_LANG_FLAG_MAP[lc] || lc;
}
function NavFlag({lang}: {lang: string}) {
  const baseUrl = getBootstrapData().settings.base_url || '';
  return (
    <img
      src={`${baseUrl}/flags/${navFlagFor(lang)}.svg`}
      style={{height: 14, width: 20}}
      className="shrink-0 rounded-sm border border-divider object-cover"
      alt={lang}
      loading="lazy"
    />
  );
}

function NavbarLocaleSwitcher() {
  const {localeCode} = useSelectedLocale();
  const changeLocale = useChangeLocale();
  const siteLocales = useBootstrapDataStore(s => s.data.i18n.locales);
  const {i18n} = useSettings();

  if (!siteLocales || siteLocales.length < 2 || !localeCode || !i18n.enable) {
    return null;
  }

  // Sort A → Z so menu order is predictable.
  const sortedLocales = [...siteLocales].sort((a, b) => a.language.localeCompare(b.language));

  return (
    <MenuTrigger
      selectionMode="single"
      selectedValue={localeCode}
      onSelectionChange={value => {
        const newLocale = value as string;
        if (newLocale !== localeCode) {
          changeLocale.mutate({locale: newLocale});
        }
      }}
    >
      {/* data-locale-switcher = Effects → Language switcher toggle hook. */}
      <IconButton
        aria-label="Change language"
        disabled={changeLocale.isPending}
        data-locale-switcher
      >
        <NavFlag lang={localeCode} />
      </IconButton>
      <Menu>
        {sortedLocales.map(locale => (
          <MenuItem
            value={locale.language}
            key={locale.language}
            className="capitalize"
            startIcon={<NavFlag lang={locale.language} />}
          >
            {locale.name}
          </MenuItem>
        ))}
      </Menu>
    </MenuTrigger>
  );
}

function getColorStyle(color: string, textColor?: string): string {
  switch (color) {
    case 'primary':
      return `bg-primary ${textColor || 'text-on-primary'} border-b-primary`;
    case 'bg':
      return `bg ${textColor || 'text-main'} border-b`;
    case 'bg-elevated':
      return `bg-elevated ${textColor || 'text-main'} border-b`;
    case 'bg-alt':
      return `bg-alt ${textColor || 'text-main'} border-b`;
    case 'transparent':
      return `bg-transparent ${textColor || 'text-white'}`;
    default:
      return `${color} ${textColor}`;
  }
}

function resolveLogoColor({
  logoColor,
  navbarColor,
  isDarkMode,
}: {
  logoColor?: 'light' | 'dark';
  navbarColor: NavbarColor;
  isDarkMode: boolean;
}): 'dark' | 'light' {
  if (logoColor != null) {
    return logoColor;
  }

  if (isDarkMode) {
    return 'light';
  }

  if (navbarColor === 'bg' || navbarColor === 'bg-alt') {
    return 'dark';
  }

  return 'light';
}
